import DomElementsBase from "@grindex-common/abstracts/DomElementsBase.class";
import { isVisible, showElement, hideElement } from "@grindex-common/helpers/domManipulation";
import { $elementChild } from "@grindex-common/helpers/elements";
import { classes } from "@grindex-common/helpers/cssGlobals";
import type { SearchTab } from "@grindex-client/components/Search/helpers/search-tab";

const SELECTOR_PAGER_TOP = ".sp-top";
const SELECTOR_PAGER_BOTTOM = ".sp-bottom";
const SELECTOR_CONTAINER_RESULTS = ".search-results";
const SELECTOR_FILTER_PANEL = ".facet-container";
const SELECTOR_BUTTON_REFINE = "button.refine";
const SELECTOR_TOTAL_RESULTS = "total-result-count";
const SELECTOR_TAB_TOTAL_COUNT = "[data-tab-count]";
const SELECTOR_CHECKBOXES = ".single-result input[type='checkbox']";
const SELECTOR_CLEAR_FACETS_CONTAINER = ".clear-search-facets-container";
const SELECTOR_CLEAR_FACETS_BUTTON = ".clear-facets-btn";
const SELECTOR_NO_RESULTS = ".search-no-results";
const SELECTOR_CLOSE_PANEL = ".close-panel";
const SELECTOR_HIDDEN_ON_NO_RESULTS = ".hidden-on-no-results";
const SELECTOR_HIDDEN_ON_RESULTS = ".hidden-on-results";
const SELECTOR_RESULT_HEADER = ".resultsHeader";
const SELECTOR_INITIAL_PAGELOAD_TITLE = ".initial-pageLoad-title";

export default class SearchBaseDom extends DomElementsBase {
	constructor($module) {
		const selectors = [
			SELECTOR_BUTTON_REFINE,
			SELECTOR_CONTAINER_RESULTS,
			SELECTOR_FILTER_PANEL,
			SELECTOR_PAGER_BOTTOM,
			SELECTOR_PAGER_TOP,
			SELECTOR_TOTAL_RESULTS,
			classes.HIDDEN,
			SELECTOR_CHECKBOXES,
			SELECTOR_CLEAR_FACETS_CONTAINER,
			SELECTOR_CLEAR_FACETS_BUTTON,
			SELECTOR_NO_RESULTS,
			SELECTOR_CLOSE_PANEL,
			SELECTOR_HIDDEN_ON_NO_RESULTS,
			SELECTOR_HIDDEN_ON_RESULTS,
			SELECTOR_TAB_TOTAL_COUNT,
		];

		super($module, selectors);

		this.$overlay = $elementChild(this.$module, "xyl-overlay-search");
		this.$loader = $elementChild(this.$module, "loader");

		// TODO: Legacy rules, handle here until they are completely removed
		this.$loader.style.display = "block";
	}

	getHiddenOnNoResultsElements() {
		return this.getElements(SELECTOR_HIDDEN_ON_NO_RESULTS);
	}

	getHiddenOnResultsElements() {
		return this.getElements(SELECTOR_HIDDEN_ON_RESULTS);
	}

	getClosePanelElement() {
		return this.getElement(SELECTOR_CLOSE_PANEL);
	}

	getNoResultsContainerElement() {
		return this.getElement(SELECTOR_NO_RESULTS);
	}

	getInitialPageLoadContainerElement() {
		return this.getElement(SELECTOR_INITIAL_PAGELOAD_TITLE);
	}

	getResultHeaderElement() {
		return this.getElement(SELECTOR_RESULT_HEADER);
	}

	getClearFacetsButton() {
		return this.getElement(SELECTOR_CLEAR_FACETS_BUTTON, true);
	}

	getRefineButtonElement() {
		return this.getElement(SELECTOR_BUTTON_REFINE);
	}

	getResultsContainerElement() {
		return this.getElement(SELECTOR_CONTAINER_RESULTS);
	}

	getFilterPanelElement() {
		return this.getElement(SELECTOR_FILTER_PANEL);
	}

	getBottomPagerElement() {
		return this.getElement(SELECTOR_PAGER_BOTTOM);
	}

	getTopPagerElement() {
		return this.getElement(SELECTOR_PAGER_TOP);
	}

	getTotalResultsElement() {
		return this.getElement(SELECTOR_TOTAL_RESULTS);
	}

	getCheckBoxElements() {
		return this.getElements(SELECTOR_CHECKBOXES, true);
	}

	getClearFacetsContainerElement() {
		return this.getElement(SELECTOR_CLEAR_FACETS_CONTAINER);
	}

	openFilterPanel() {
		this.getFilterPanelElement().classList.toggle("open");
	}

	toggleLoader() {
		if (this.$loader) {
			this.$loader.classList.toggle(classes.HIDDEN);
		}
	}

	toggleOverlay() {
		if (this.$overlay) {
			this.$overlay.classList.toggle(classes.HIDDEN);
		}
	}

	createClearFacetsButton() {
		const $clearFacetsButton = document.createElement("a");
		$clearFacetsButton.className = "button disabled clear-facets-btn";
		$clearFacetsButton.innerHTML = window.config.search.clearAllFacetsLabel;

		return $clearFacetsButton;
	}

	addClearFacetsButtonToContainer() {
		const $clearFacetsButton = this.createClearFacetsButton();
		this.getClearFacetsContainerElement().innerHTML = "";
		this.getClearFacetsContainerElement().append($clearFacetsButton);
	}

	setTotalResultsText(text, tab: SearchTab | undefined) {
		const $resultElement = document.querySelector(`[data-tab-count='${tab}']`);
		if ($resultElement) {
			$resultElement.innerText = `(${text})`;
		} else if (this.getTotalResultsElement()) {
			this.getTotalResultsElement().innerText = `(${text})`;
		}
	}

	getResultItem(itemId) {
		return this.$module.querySelector(`[data-result-id="${itemId}"]`);
	}

	hideResultItem(itemId) {
		hideElement(this.$module.querySelector(`[data-result-id="${itemId}"]`));
	}

	toggleClearFacetsButton(enabled) {
		const $clearFacetsBtn = this.getClearFacetsButton();

		if ($clearFacetsBtn) {
			if (enabled) {
				$clearFacetsBtn.classList.remove("disabled");
			} else {
				$clearFacetsBtn.classList.add("disabled");
			}
		}
	}

	showElementsHiddenOnNoResults() {
		this.getHiddenOnNoResultsElements().forEach(($element) => {
			$element.classList.remove(classes.HIDDEN);
		});
	}

	hideElementsHiddenOnResults() {
		this.getHiddenOnResultsElements().forEach(($element) => {
			$element.classList.add(classes.HIDDEN);
		});
	}

	showNoResults() {
		if (this.getInitialPageLoadContainerElement()) {
			hideElement(this.getInitialPageLoadContainerElement());
		}

		if (this.getNoResultsContainerElement()) {
			showElement(this.getNoResultsContainerElement());
		}

		if (this.getResultHeaderElement()) {
			hideElement(this.getResultHeaderElement());
		}
	}

	hideNoResults() {
		if (this.getInitialPageLoadContainerElement()) {
			hideElement(this.getInitialPageLoadContainerElement());
		}

		if (this.getNoResultsContainerElement()) {
			hideElement(this.getNoResultsContainerElement());
		}

		if (this.getResultHeaderElement()) {
			showElement(this.getResultHeaderElement());
		}
	}

	addToggleFilterPanelEvents($element) {
		if ($element) {
			$element.addEventListener("click", () => {
				this.openFilterPanel();
				this.updateFilterTop(0);
			});
		}
	}

	updateFilterTop(delay) {
		var timeout = window.setTimeout(
			function () {
				this.adjustFilterPanelTop();
				clearTimeout(timeout);
			}.bind(this),
			delay,
		);
	}

	adjustFilterPanelTop() {
		if (isVisible(this.getRefineButtonElement())) {
			// ---> TECH DEBT -> Should be handled by css and not script
			this.getFilterPanelElement().style.top = this.getRefineButtonElement().offsetTop + "px";
		}
	}
}
